import React, { useState, useEffect, useRef } from "react";

import { RichTextModule } from "components/RichTextModule";
import { ContentModuleBaseAttributes } from "utils/orm";
import {
    getJWPlayerVideoInfo,
    initJWPlayerVideo,
    initJWPlayerTracking,
} from "utils/jwPlayer";

export interface JWVideoBlock extends ContentModuleBaseAttributes {
    jwVideo: string;
    richText?: string;
    autostart?: boolean;
    position?: string;
}

export const JWVideoModule: React.FC<JWVideoBlock> = ({
    jwVideo,
    richText,
    heading,
    autostart = false,
    position = "post_individual_body"
}) => {
    const jwPlayerContainer = useRef(null);
    const jwPlayerWrapper = useRef(null);
    const [videoInfo, setVideoInfo] = useState(null);
    const [playerInstance, setPlayerInstance] = useState(null);
    const [trackingProps, setTrackingProps] = useState(null);

    // Get the video info, then init the player instance.
    useEffect(() => {
        getJWPlayerVideoInfo(jwVideo)
            .then((video) => {
                setVideoInfo(video);
                const instance = initJWPlayerVideo({
                    videoInfo: video,
                    autostart,
                    container: jwPlayerContainer,
                    mute: !!autostart
                });
                setPlayerInstance(instance);
            })
            .catch((error) => {
                console.error("Error occurred while fetching video info:", error);
                setVideoInfo(null);
                setPlayerInstance(null);
            });
    }, [jwVideo, autostart]);

    // Init tracking props if the player was successfully instantiated.
    useEffect(() => {
        if (playerInstance) {
            setTrackingProps({
                "data-tracking-props": JSON.stringify({
                    sp_impression_component: "insert",
                    sp_impression_insert_type: "media",
                    sp_impression_position: "in_article",
                    sp_media_type: "video",
                    sp_media_name: videoInfo?.title ?? null,
                    sp_media_provider_key: jwVideo ?? null,
                    sp_media_provider_name: "jwplayer",
                })
            });
            initJWPlayerTracking({
                playerInstance,
                videoInfo,
                position,
                element: jwPlayerWrapper?.current
            });
        }
    }, [videoInfo, playerInstance, jwVideo, position]);

    if (!jwVideo) return null;

    return (
        <div className="jwplayerModule" {...trackingProps} ref={jwPlayerWrapper}>
            {heading && <h1>{heading}</h1>}
            <div
                id={`jwplayer-${jwVideo}`}
                className="jwplayer-container"
                ref={jwPlayerContainer}
            />
            {richText && <RichTextModule type="richTextBlock" richText={richText} />}
        </div>
    );
};
